// src/AuthContext.tsx

import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
  useCallback,
} from "react";
import { onAuthStateChanged, User } from "firebase/auth";
import { auth, db } from "./firebase";
import { doc, getDoc, collection, getDocs, query, where, documentId, setDoc } from "firebase/firestore";

type BarRef = {
  id: string;
  name?: string;
  address?: string;
  placeId?: string;
  ownerId?: string;
  charityId?: string;
  claimed?: boolean;
  claimedAt?: any;
};

type AuthContextType = {
  user: User | null;
  role: string | null;
  userBars: BarRef[];
  primaryBarId: string | null;
  loadingAuth: boolean;
  refreshUserBars: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextType | null>(null);

type ProviderProps = {
  children: ReactNode;
};

export function AuthProvider({ children }: ProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [userBars, setUserBars] = useState<BarRef[]>([]);
  const [primaryBarId, setPrimaryBarId] = useState<string | null>(null);
  const [loadingAuth, setLoadingAuth] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser || null);
      setLoadingAuth(false);
    });

    return () => unsub();
  }, []);

  const loadUserData = useCallback(async (currentUser: User | null) => {
    if (!currentUser) {
      setRole(null);
      setUserBars([]);
      setPrimaryBarId(null);
      return;
    }

    let detectedRole: string | null = null;
    let detectedPrimaryBar: string | null = null;
    let claimedBars: string[] = [];
    let barIdsFromRelations: string[] = [];

    const userRef = doc(db, "users", currentUser.uid);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      const data = userSnap.data() as any;
      // Keep the saved email in step with the login email (it changes when
      // someone updates their email under Account). Admin tools look people
      // up by this email.
      if (currentUser.email && data.email !== currentUser.email.toLowerCase()) {
        setDoc(userRef, { email: currentUser.email.toLowerCase() }, { merge: true }).catch(() => {});
      }
      if (data.role) detectedRole = data.role;
      if (data.primaryBarId) detectedPrimaryBar = data.primaryBarId;
      if (Array.isArray(data.claimedBars)) {
        claimedBars = data.claimedBars;
      }
    }

    const barUsersQ = query(
      collection(db, "barUsers"),
      where("userId", "==", currentUser.uid)
    );
    const barUsersSnap = await getDocs(barUsersQ);

    let highestRole: string | null = detectedRole;
    barIdsFromRelations = barUsersSnap.docs.map((d) => {
      const bu = d.data() as any;
      const r = bu.role as string | undefined;
      if (r) {
        if (!highestRole) highestRole = r;
        if (highestRole === "staff" && (r === "manager" || r === "owner")) {
          highestRole = r;
        }
        if (highestRole === "manager" && r === "owner") {
          highestRole = r;
        }
      }
      return bu.barId as string;
    });

    const ownedBarsQ = query(
      collection(db, "bars"),
      where("ownerId", "==", currentUser.uid)
    );
    const ownedBarsSnap = await getDocs(ownedBarsQ);
    const ownedBars: BarRef[] = ownedBarsSnap.docs.map((d) => ({
      id: d.id,
      ...(d.data() as any),
    }));

    const charityBarsQ = query(
      collection(db, "bars"),
      where("charityId", "==", currentUser.uid)
    );
    const charityBarsSnap = await getDocs(charityBarsQ);
    const charityBars: BarRef[] = charityBarsSnap.docs.map((d) => ({
      id: d.id,
      ...(d.data() as any),
    }));

    const uniqueBarIds = Array.from(
      new Set([
        ...claimedBars,
        ...barIdsFromRelations,
        ...ownedBars.map((b) => b.id),
        ...charityBars.map((b) => b.id),
      ])
    );

    let relatedBars: BarRef[] = [...ownedBars, ...charityBars];

    if (uniqueBarIds.length > 0) {
      // Only fetch the bars this person is linked to (10 per request),
      // never the whole bars list — that list is now every bar in the US.
      const have = new Set([...ownedBars, ...charityBars].map((b) => b.id));
      // Skip blank or broken ids: one bad id would make the whole lookup fail
      // and bounce the person back to the login page.
      const missing = uniqueBarIds.filter(
        (id) => typeof id === "string" && id.trim() !== "" && !id.includes("/") && !have.has(id)
      );
      const fromRelations: BarRef[] = [];
      for (let i = 0; i < missing.length; i += 10) {
        try {
          const chunkSnap = await getDocs(
            query(collection(db, "bars"), where(documentId(), "in", missing.slice(i, i + 10)))
          );
          chunkSnap.docs.forEach((d) =>
            fromRelations.push({ id: d.id, ...(d.data() as any) } as BarRef)
          );
        } catch (err) {
          console.error("Couldn't load some linked bars:", err);
        }
      }

      const mergedMap = new Map<string, BarRef>();
      [...ownedBars, ...charityBars, ...fromRelations].forEach((b) => {
        mergedMap.set(b.id, b);
      });
      relatedBars = Array.from(mergedMap.values());
    }

    let finalRole = highestRole;
    if (!finalRole && ownedBars.length > 0) {
      finalRole = "owner";
    }
    if (!finalRole && charityBars.length > 0) {
      finalRole = "charity";
    }
    if (!finalRole && barUsersSnap.docs.length > 0) {
      const first = barUsersSnap.docs[0].data() as any;
      finalRole = first.role || null;
    }

    let finalPrimaryBar = detectedPrimaryBar;
    if (!finalPrimaryBar) {
      if (claimedBars.length > 0) {
        finalPrimaryBar = claimedBars[0];
      } else if (barIdsFromRelations.length > 0) {
        finalPrimaryBar = barIdsFromRelations[0];
      } else if (relatedBars.length > 0) {
        finalPrimaryBar = relatedBars[0].id;
      }
    }

    setRole(finalRole || null);
    setPrimaryBarId(finalPrimaryBar || null);
    setUserBars(relatedBars);
  }, []);

  useEffect(() => {
    if (loadingAuth) return;

    setLoadingAuth(true);
    loadUserData(user).finally(() => setLoadingAuth(false));
  }, [user, loadUserData]);

  const refreshUserBars = useCallback(async () => {
    await loadUserData(user);
  }, [user, loadUserData]);

  if (loadingAuth) {
    return <div style={{ padding: 40, color: "white" }}>Loading…</div>;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        role,
        userBars,
        primaryBarId,
        loadingAuth,
        refreshUserBars,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside an AuthProvider");
  }
  return ctx;
}